import { NavLink } from 'react-router'
import { layout } from '../design/tokens'

type Onglet = {
  to: string
  libelle: string
  icone: 'aujourdhui' | 'categories' | 'outils' | 'espaces'
  /** Actif aussi sur les écrans enfants — « Outils » le reste dans l'agenda. */
  prefixe?: string
}

/**
 * Barre de navigation basse — décision L49.
 *
 * Elle était réduite à trois entrées, et tout le reste vivait sur l'écran d'un espace : outils,
 * catégories et leur gestion s'y empilaient. Quatre destinations et un bouton d'ajout central
 * permettent de dégraisser cet écran et de rejoindre n'importe quelle section en un geste.
 *
 * Flottante, détachée des bords : c'est l'usage des applications qu'on a dans la main, et le
 * seul écart au principe « la séparation se fait par la bordure, pas par la profondeur » —
 * assumé, avec l'ombre déjà déclarée pour les feuilles modales, sans en inventer une nouvelle.
 */
export function TabBar({ onAdd, activeSpaceId }: { onAdd: () => void; activeSpaceId?: string | null }) {
  const espace = activeSpaceId ?? null

  const gauche: Onglet[] = [
    { to: '/', libelle: "Aujourd'hui", icone: 'aujourdhui' },
    {
      to: espace ? `/espace/${espace}` : '/espaces',
      libelle: 'Catégories',
      icone: 'categories',
      ...(espace ? { prefixe: `/espace/${espace}/recents` } : {}),
    },
  ]

  const droite: Onglet[] = [
    {
      to: espace ? `/espace/${espace}/outils` : '/espaces',
      libelle: 'Outils',
      icone: 'outils',
      ...(espace ? { prefixe: `/espace/${espace}/` } : {}),
    },
    { to: '/espaces', libelle: 'Espaces', icone: 'espaces' },
  ]

  return (
    <nav
      className="fixed inset-x-0 mx-auto flex items-center justify-around rounded-full border border-border shadow-sheet"
      style={{
        // Détachée du bas, et de la zone sûre du téléphone : une barre collée au bord tombe
        // sous la barre de geste sur iPhone.
        bottom: `calc(${layout.tabBarInset}px + env(safe-area-inset-bottom))`,
        left: layout.tabBarInset,
        right: layout.tabBarInset,
        maxWidth: layout.column - layout.tabBarInset * 2,
        height: layout.tabBar,
        paddingInline: 8,
        // Translucide et floutée : la barre reste lisible tout en laissant deviner ce qu'elle
        // couvre, ce qui la fait paraître moins haute qu'elle ne l'est. Le repli — un fond
        // presque opaque — s'applique seul là où le flou n'existe pas, sans rien casser.
        background: 'color-mix(in srgb, var(--color-card) 80%, transparent)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      {gauche.map((onglet) => (
        <Bouton key={onglet.libelle} onglet={onglet} />
      ))}

      {/* Le geste central garde sa place et son sens : il ajoute ce que l'écran contient. */}
      <button
        type="button"
        onClick={onAdd}
        aria-label="Ajouter"
        className="flex shrink-0 items-center justify-center rounded-full bg-accent font-semibold text-card"
        style={{ width: 46, height: 46, fontSize: 24, lineHeight: 1 }}
      >
        +
      </button>

      {droite.map((onglet) => (
        <Bouton key={onglet.libelle} onglet={onglet} />
      ))}
    </nav>
  )
}

function Bouton({ onglet }: { onglet: Onglet }) {
  return (
    <NavLink
      to={onglet.to}
      end={onglet.prefixe === undefined}
      aria-label={onglet.libelle}
      className="flex shrink-0 flex-col items-center justify-center gap-1"
      style={{ minWidth: 56, minHeight: layout.minimumTouchTarget }}
    >
      {({ isActive }) => {
        const actif =
          isActive ||
          (onglet.prefixe !== undefined && window.location.pathname.startsWith(onglet.prefixe))

        return (
          <>
            <Icone nom={onglet.icone} actif={actif} />
            {/* Le libellé reste sous l'icône : une icône seule s'apprend, mais pas du premier
                coup, et cette application n'est pas utilisée tous les jours par tout le monde. */}
            <span
              className="text-label font-semibold"
              style={{
                fontSize: 10,
                letterSpacing: 0,
                color: actif ? 'var(--color-accent)' : 'var(--color-ink-muted)',
              }}
            >
              {onglet.libelle}
            </span>
          </>
        )
      }}
    </NavLink>
  )
}

/** Traits fins, comme les icônes d'outils : l'aplat de couleur reste au contenu. */
function Icone({ nom, actif }: { nom: Onglet['icone']; actif: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={actif ? 'var(--color-accent)' : 'var(--color-ink-muted)'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {nom === 'aujourdhui' ? (
        <>
          <path d="M3.5 11 12 4l8.5 7" />
          <path d="M5.5 9.5V19a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.5" />
        </>
      ) : nom === 'categories' ? (
        <>
          <rect x="3.5" y="3.5" width="7.5" height="7.5" rx="1.5" />
          <rect x="13" y="3.5" width="7.5" height="7.5" rx="1.5" />
          <rect x="3.5" y="13" width="7.5" height="7.5" rx="1.5" />
          <rect x="13" y="13" width="7.5" height="7.5" rx="1.5" />
        </>
      ) : nom === 'outils' ? (
        <>
          <path d="M14.5 5.5a3.5 3.5 0 0 0 4.6 4.6L21 12l-9 9-3-3 9-9z" />
          <path d="m6.5 6.5 3 3" />
          <path d="M3 10.5 6.5 7 3 3.5 5 3l3.5 3.5" />
        </>
      ) : (
        <>
          <circle cx="9" cy="8" r="3" />
          <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
          <path d="M16 5.5a3 3 0 0 1 0 5" />
          <path d="M17.5 14.2a5 5 0 0 1 3 4.8" />
        </>
      )}
    </svg>
  )
}
